import React, { useCallback, useEffect, useMemo, useState } from 'react';

import classnames from 'classnames';

import Drawer from 'components/uiLibrary/Drawer';
import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import LinkButton, { TextButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Tooltip from 'components/uiLibrary/Tooltip';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, GOOGLE_OLD_TRACKING_SERVICES } from 'components/Globals/Analytics/constants';
import CastingResultTypeTabs from 'components/Globals/Layout/Header/CastingSearch/CastingResultTypeTabs';
import WorkRoleListing from 'components/Work/Display/WorkRoleListing';

import {
  BOOLEAN_SEARCH_PARTS,
  CASTING_TOOL_SLUGS,
  CASTING_TOOL_TABS,
  DATE_FORMATS,
  DEFAULT_BOOLEAN_QUERY,
  TP,
  URL_SLUG_TYPES,
} from 'constants/index';
import { CASTING_SEARCH_CATEGORIES, CASTING_SEARCH_CATEGORIES_KEYS } from 'constants/search';

import { cleanBooleanQuery, getBooleanSearchQuery } from 'utils/casting';
import { createDate } from 'utils/date';
import { generateFilterParams } from 'utils/globals/filters';
import useAppContext from 'utils/hooks/useAppContext';
import usePageContext from 'utils/hooks/usePageContext';
import { trackClearForm, trackSearchGo } from 'utils/tracking';

import { Trans, useTranslation } from 'src/i18n';

import { CASTING_RESULT_TYPES_VALUES, FILTER_TYPES, ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';

import CastingInput from './CastingInput';
// eslint-disable-next-line import/no-cycle
import MobileSearchSelected from './MobileSearchSelected';
import classes from './RoleRepertoireDrawer.module.scss';
import SearchDrawer from './SearchDrawer';

const SearchCategories = ({
  onChangeHandler,
  countries,
  sinceYear,
  booleanQuery,
  composer,
  setComposer,
  trackingData,
}) => {
  const { t } = useTranslation('NS_CASTING_TOOL');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext;
  const { tab } = linkProps || {};
  const { value } = booleanQuery || {};
  const isDesiredRole = tab === CASTING_RESULT_TYPES_VALUES.DESIRED_ROLE;
  const searchCategories = useMemo(() => {
    if (isDesiredRole) {
      return CASTING_SEARCH_CATEGORIES.filter(category =>
        [CASTING_SEARCH_CATEGORIES_KEYS.CREATORS, CASTING_SEARCH_CATEGORIES_KEYS.WORKS].includes(category.key),
      );
    }

    return CASTING_SEARCH_CATEGORIES.filter(category => category.key !== CASTING_SEARCH_CATEGORIES_KEYS.ROLES);
  }, [isDesiredRole]);

  return (
    <>
      {searchCategories.map(category => (
        <div className={classes.search__field} key={category.key}>
          <Typography size="12" weight="medium" truncate>
            {t(category.label)}
            {!!category?.helpText && (
              <Tooltip title={t(category.helpText)}>
                <span>
                  <SpriteIcon icon="help" size={14} className={classes.search__helpIcon} />
                </span>
              </Tooltip>
            )}
          </Typography>
          <CastingInput
            setSelectedCategory={setSelectedCategory}
            category={category}
            sinceYear={sinceYear}
            countries={countries}
            workId={value?.[URL_SLUG_TYPES.WORK]}
            composer={composer}
          />
        </div>
      ))}
      {selectedCategory?.key && (
        <SearchDrawer
          category={selectedCategory}
          onClose={() => setSelectedCategory(null)}
          onClick={onChangeHandler}
          countries={countries}
          sinceYear={sinceYear}
          composer={composer}
          booleanQuery={booleanQuery}
          setComposer={setComposer}
          workId={value?.[URL_SLUG_TYPES.WORK]}
          trackingData={trackingData}
        />
      )}
    </>
  );
};

const MobileSearch = ({
  showRoleSelection,
  setShowRoleSelection,
  workId,
  filtersParams,
  onChangeHandler,
  countries,
  sinceYear,
  booleanQuery,
  composer,
  setComposer,
  trackingData,
}) => {
  const { t } = useTranslation('NS_CASTING_TOOL');

  if (showRoleSelection) {
    return (
      <div>
        <div className={classes.selectedSearch} onClick={() => setShowRoleSelection(false)}>
          <Typography size="13" weight="bold" color="link">
            {t(`${TP}.m_UPDATE`)}
          </Typography>
        </div>
        <div className={classes.divider} />
        <Typography size="12" weight="bold" variant="p" className={classes.drawer__roleListing_title}>
          {t(`${TP}.SELECT_ROLE_TO_PROCEED`)}
        </Typography>
        <WorkRoleListing
          workId={workId}
          filters={filtersParams}
          onSelect={role => {
            onChangeHandler(CASTING_TOOL_SLUGS.WORK_ROLE, role);
            setShowRoleSelection(false);
          }}
          className={classes.drawer__roleListing}
        />
      </div>
    );
  }

  return (
    <SearchCategories
      onChangeHandler={onChangeHandler}
      countries={countries}
      sinceYear={sinceYear}
      booleanQuery={booleanQuery[0]}
      composer={composer}
      setComposer={setComposer}
      trackingData={trackingData}
    />
  );
};

const RoleRepertoireDrawer = ({ onClose, trackingData }) => {
  const { t } = useTranslation('NS_CASTING_TOOL');
  const track = useTracking();
  const { obRouteContext } = useAppContext();
  const { applyFilters, resetFilters } = usePageContext();
  const { filtersParams } = obRouteContext || {};
  const { filters, tab } = obRouteContext?.linkProps || {};
  const defaultYear = createDate()
    .subtract(5, 'year')
    .format(DATE_FORMATS.YEAR);
  const [sinceYear, setSinceYear] = useState(() => filters?.[FILTER_TYPES.SINCE_YEAR]?.[0]?.id || defaultYear);
  const [countries, setCountries] = useState(() => {
    if (filters?.[FILTER_TYPES.COUNTRY]) {
      return filters[FILTER_TYPES.COUNTRY];
    }

    return null;
  });
  const [composer, setComposer] = useState(null);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const parsedQuery = useMemo(() => getBooleanSearchQuery(filters?.expression), [filters?.expression]);
  const [booleanQuery, setBooleanQuery] = useState(parsedQuery?.length > 0 ? parsedQuery : [DEFAULT_BOOLEAN_QUERY]);
  const workId = booleanQuery?.[0]?.value?.[URL_SLUG_TYPES.WORK];
  const roleId = booleanQuery?.[0]?.value?.[URL_SLUG_TYPES.WORK_ROLE];
  const activeTabKey = tab;
  const isRoleDebut = activeTabKey === CASTING_RESULT_TYPES_VALUES.ROLE_DEBUT;
  const isDesiredRole = activeTabKey === CASTING_RESULT_TYPES_VALUES.DESIRED_ROLE;
  const activeTab = CASTING_TOOL_TABS.find(item => item.key === activeTabKey);

  const onChangeHandler = useCallback(
    (key, value, index = 0) => {
      if (key === FILTER_TYPES.SINCE_YEAR) {
        setSinceYear(value?.id);
        setCountries([]);
      } else if (key === FILTER_TYPES.COUNTRY) {
        setCountries(value);
      } else {
        const tempData = [...booleanQuery];
        tempData[index] = {
          type: BOOLEAN_SEARCH_PARTS.QUERY,
          value: {
            ...tempData[index]?.value,
            [key]: key === CASTING_TOOL_SLUGS.WORK_ROLE ? { id: value?.id, type: value?.type } : value?.id,
            ...(key === CASTING_TOOL_SLUGS.WORK && {
              [CASTING_TOOL_SLUGS.WORK_ROLE]: null,
            }),
          },
        };
        setBooleanQuery(tempData);
      }
    },
    [booleanQuery],
  );

  const onSearch = useCallback(() => {
    const cleanedQuery = cleanBooleanQuery(booleanQuery);
    const filterDetails = generateFilterParams({ booleanQuery: cleanedQuery, sinceYear, countries, isRoleDebut });
    track.click(trackSearchGo(filterDetails), GOOGLE_OLD_TRACKING_SERVICES);

    if (!roleId) {
      setShowRoleSelection(true);
      return;
    }

    applyFilters({
      baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
      filters: {
        ...filters,
        ...filterDetails,
      },
      tab: activeTabKey,
    });
    onClose();
  }, [activeTabKey, applyFilters, booleanQuery, countries, filters, isRoleDebut, roleId, sinceYear, track, onClose]);

  const onDeleteHandler = useCallback(
    index => {
      const tempQuery = [...booleanQuery]?.filter((_, i) => i !== index && i !== index - 1);
      setBooleanQuery(tempQuery);
    },
    [booleanQuery],
  );

  const onClearSearch = useCallback(() => {
    resetFilters();
    setBooleanQuery([DEFAULT_BOOLEAN_QUERY]);
    setSinceYear(defaultYear);
    setCountries(null);
    setComposer(null);
    track.click(trackClearForm(), GOOGLE_OLD_TRACKING_SERVICES);
  }, [resetFilters, track, defaultYear, setComposer]);

  useEffect(() => {
    setBooleanQuery(parsedQuery?.length > 0 ? parsedQuery : [DEFAULT_BOOLEAN_QUERY]);
  }, [parsedQuery]);

  return (
    <Drawer isOpen onClose={onClose} className={classes.drawer}>
      <div className={classes.drawer__header}>
        <SpriteIcon icon="arrow_back" size="16" onClick={onClose} />
        <Typography weight="bold" className={classes.drawer__header_title}>
          {t(`${TP}.ROLE_REPERTOIRE_SEARCH`)}
        </Typography>
        <SpriteIcon icon="close" size="16" onClick={onClose} />
      </div>
      <HorizontalScroller hideArrow>
        <div className={classes.drawer__tabs}>
          <CastingResultTypeTabs
            trackingData={{
              ...trackingData,
              component: COMPONENTS.TABS,
            }}
          />
        </div>
      </HorizontalScroller>
      <div
        className={classnames(classes.drawer__content, { [classes.drawer__content_roleSelection]: showRoleSelection })}
      >
        {!showRoleSelection && (
          <Typography size="13" weight="medium" className={classes.drawer__content_description} variant="p">
            <Trans i18nKey={t(activeTab?.description)} ns="NS_APP_GLOBALS" components={{ b: <b /> }} />
          </Typography>
        )}
        <div className={classes.drawer__content_search}>
          {roleId ? (
            <MobileSearchSelected
              booleanQuery={booleanQuery}
              setBooleanQuery={setBooleanQuery}
              onDeleteHandler={onDeleteHandler}
              sinceYear={sinceYear}
              countries={countries}
              onChangeHandler={onChangeHandler}
              composer={composer}
              setComposer={setComposer}
              isDesiredRole={isDesiredRole}
              isRoleDebut={isRoleDebut}
              trackingData={trackingData}
            />
          ) : (
            <MobileSearch
              showRoleSelection={showRoleSelection}
              setShowRoleSelection={setShowRoleSelection}
              workId={workId}
              filtersParams={filtersParams}
              onChangeHandler={onChangeHandler}
              countries={countries}
              sinceYear={sinceYear}
              booleanQuery={booleanQuery}
              composer={composer}
              setComposer={setComposer}
              trackingData={trackingData}
            />
          )}
        </div>
      </div>
      <div className={classes.drawer__actions}>
        <TextButton
          leftIcon={<SpriteIcon icon="replay-bold" size="16" className={classes.clearIcon} />}
          variant="tertiary"
          onClick={onClearSearch}
          trackingData={{ component: COMPONENTS.CLEAR_CTA, ...trackingData }}
        >
          {t(`${TP}.FORM_CLEAR_ALL`)}
        </TextButton>
        <LinkButton
          variant="orange"
          onClick={onSearch}
          disabled={showRoleSelection ? !roleId : !workId}
          styles={{ root: classes.searchButton }}
          trackingData={{ component: COMPONENTS.SUBMIT_CTA, ...trackingData }}
        >
          {t(`${TP}.SEARCH`)}
        </LinkButton>
      </div>
    </Drawer>
  );
};

export default RoleRepertoireDrawer;
